import React, { useEffect, useRef, useState } from 'react';

import { mirrorNormalizedPoint } from 'business/quality-control/pages/record-segment-defect/utils/mirror-normalized-point';
import { BoardViewMode } from 'ui/quality-control/board/utils';

export type DrawingPoint = { x: number; y: number };

export type Stroke = {
  id: string;
  segmentId: string;
  points: DrawingPoint[];
  normalizedPoints: DrawingPoint[];
};

// transform the user position to a point defined by coord (x; y)
const transformPositionToPointCoord = (
  event: { clientX: number; clientY: number },
  svg: SVGSVGElement,
): DrawingPoint => {
  const point = svg.createSVGPoint();
  point.x = event.clientX;
  point.y = event.clientY;
  const svgPoint = point.matrixTransform(svg.getScreenCTM()!.inverse());

  return { x: svgPoint.x, y: svgPoint.y };
};

// transform a list of coord (x; y) to a string displayable on a svg
const pointCoordsToDraw = (points: DrawingPoint[]) =>
  points.map((p) => `${p.x},${p.y}`).join(' ');

export const useDrawing = ({
  isDrawingEnable,
  strokeResetKey,
  segmentWidth,
  segmentHeight,
  boardViewMode,
  onDrawStart,
  onStrokeComplete,
}: {
  isDrawingEnable: boolean;
  strokeResetKey: number;
  segmentWidth: number;
  segmentHeight: number;
  boardViewMode: BoardViewMode;
  onDrawStart?: (segmentId: string) => void;
  onStrokeComplete?: (stroke: Stroke) => void;
}) => {
  const svgRef = useRef<SVGSVGElement>(null);
  const liveLineRef = useRef<SVGPolylineElement>(null);

  const currentPointsRef = useRef<DrawingPoint[]>([]);
  const currentSegmentIdRef = useRef<string | null>(null);

  const [strokes, setStrokes] = useState<Stroke[]>([]);

  const resetDrawing = () => {
    liveLineRef.current?.setAttribute('points', '');
    currentPointsRef.current = [];
    currentSegmentIdRef.current = null;
  };

  useEffect(() => {
    if (isDrawingEnable) {
      setStrokes([]);
    } else {
      resetDrawing();
    }
  }, [isDrawingEnable]);

  useEffect(() => {
    setStrokes([]);
  }, [strokeResetKey]);

  // start drawing, register first point
  const handlePointerDown = (e: React.PointerEvent<SVGSVGElement>) => {
    if (!isDrawingEnable) {
      return;
    }

    const target = (e.target as Element).closest('[data-id]');
    if (!target) {
      return;
    }

    const segmentId = target.getAttribute('data-id');
    if (!segmentId) {
      return;
    }

    setStrokes([]);
    onDrawStart?.(segmentId);

    const point = transformPositionToPointCoord(e, svgRef.current!);
    currentPointsRef.current = [point];
    currentSegmentIdRef.current = segmentId;

    e.currentTarget.setPointerCapture(e.pointerId);
  };

  // drawing in progress, add points to drawing line
  const handlePointerMove = (e: React.PointerEvent<SVGSVGElement>) => {
    if (!currentSegmentIdRef.current) {
      return;
    }

    const point = transformPositionToPointCoord(e, svgRef.current!);
    currentPointsRef.current.push(point);

    liveLineRef.current?.setAttribute(
      'points',
      pointCoordsToDraw(currentPointsRef.current),
    );
  };

  // end of drawing
  const handlePointerUp = () => {
    if (!currentSegmentIdRef.current || currentPointsRef.current.length < 2) {
      resetDrawing();
      return;
    }

    const segmentId = currentSegmentIdRef.current;
    const points = [...currentPointsRef.current];

    const polygon = svgRef.current!.querySelector(
      `[data-id="${segmentId}"]`,
    ) as SVGGraphicsElement;

    // getBBox() returns the axis-aligned bounding box which includes the tilt offset,
    // so bbox.width = segmentWidth + tiltValue. We use the logical dimensions instead
    // to match the reconstruction formula: absoluteX = posX + normalizedX * segmentWidth.
    const bbox = polygon.getBBox();
    const rawNormalizedPoints = points.map((p) => ({
      x: (p.x - bbox.x) / segmentWidth,
      y: (p.y - bbox.y) / segmentHeight,
    }));

    // in exit view the rendered segment is mirrored (see getExitViewRings), so a fresh
    // drawing's raw points capture the mark's exit-view position. Flip them back to the
    // canonical entry-native position before saving — same formula as the display mirror,
    // which is a self-inverse point reflection, so applying it again undoes it. The tilt
    // offset is read straight off the rendered polygon's bbox rather than recomputed from
    // shape type, since bbox.width = segmentWidth + tiltOffset regardless of which shape is
    // currently displayed (Key and KeyInverted share the same bbox width).
    const tiltFraction =
      segmentWidth > 0 ? (bbox.width - segmentWidth) / segmentWidth : 0;
    const normalizedPoints =
      boardViewMode === BoardViewMode.Exit
        ? rawNormalizedPoints.map((p) => mirrorNormalizedPoint(p, tiltFraction))
        : rawNormalizedPoints;

    const newStroke: Stroke = {
      id: crypto.randomUUID(),
      segmentId,
      points,
      normalizedPoints,
    };

    resetDrawing();
    onStrokeComplete?.(newStroke);
    setStrokes((prev) => [...prev, newStroke]);
  };

  return {
    svgRef,
    liveLineRef,
    strokes,
    handlers: {
      onPointerDown: handlePointerDown,
      onPointerMove: handlePointerMove,
      onPointerUp: handlePointerUp,
      onPointerCancel: resetDrawing,
    },
  };
};
